/** Transient simulate-mode inputs. Outside the persisted `System`, so a reload
 *  clears them. */
export type SimulateState = {
  /** Active state-option id per component (e.g. "sunny", "starting"). */
  componentStates: Record<string, string>;
  /** Battery state-of-charge as a fraction in [0, 1]. Default 0.5 when missing. */
  batterySoc: Record<string, number>;
  /** Per-load on/off. A missing entry means active. */
  loadStates: Record<string, { active: boolean }>;
};

export type SimulateActions = {
  setComponentSimulateState(nodeId: string, optionId: string): void;
  setBatterySoc(nodeId: string, soc: number): void;
  setLoadActive(nodeId: string, active: boolean): void;
};

export function initialSimulateState(): SimulateState {
  return {
    componentStates: {},
    batterySoc: {},
    loadStates: {},
  };
}

type SetFn = (
  partial:
    | { simulateState: SimulateState }
    | ((s: { simulateState: SimulateState }) => { simulateState: SimulateState }),
) => void;

export function createSimulateActions(set: SetFn): SimulateActions {
  return {
    setComponentSimulateState: (nodeId, optionId) =>
      set((s) => {
        // Selecting a declared operating state supersedes this node's generic
        // load toggle, so an old Off cannot silently disable Cruise/Full/etc.
        let loadStates = s.simulateState.loadStates;
        if (nodeId in loadStates) {
          loadStates = { ...loadStates };
          delete loadStates[nodeId];
        }
        return {
          simulateState: {
            ...s.simulateState,
            loadStates,
            componentStates: {
              ...s.simulateState.componentStates,
              [nodeId]: optionId,
            },
          },
        };
      }),
    setBatterySoc: (nodeId, soc) =>
      set((s) => ({
        simulateState: {
          ...s.simulateState,
          batterySoc: {
            ...s.simulateState.batterySoc,
            [nodeId]: Math.max(0, Math.min(1, soc)),
          },
        },
      })),
    setLoadActive: (nodeId, active) =>
      set((s) => ({
        simulateState: {
          ...s.simulateState,
          loadStates: {
            ...s.simulateState.loadStates,
            [nodeId]: { active },
          },
        },
      })),
  };
}
